export const DEV_API_URL = 'https://v2.donateou.org'
export const DEV_TEST_API_URL = 'https://donate-v2.oudev.org'
export const STAGE_API_URL = 'https://donate-v2.oudev.org'
export const PROD_API_URL = 'https://v2.donateou.org'

export const PROD_GTM_ID = 'GTM-PCWSG75'
export const STAGING_GTM_ID = 'GTM-NJTWJW2'

type ResolveClientRuntimeInput = {
  locationOrigin?: string
  scriptSrc?: string
  explicitTestMode?: boolean
}

type ResolveServerRuntimeInput = {
  refererUrl?: string
  nodeEnv?: string
  paymentApiTestMode?: string
}

type ClientRuntime = {
  apiUrl: string
  isProduction: boolean
  testMode: boolean
  gtmId: string
}

type CreditCardBooleanAttributesInput = {
  testMode: boolean
  recurring?: boolean
  noCVV?: boolean
}

const TEST_CREDIT_CARD_WIDGET_URL = 'https://cc-widget.oudev.org/credit-card.js'
const PROD_CREDIT_CARD_WIDGET_URL = 'https://cc-widget.ouapis.org/credit-card.js'
const SENSEPASS_PREVIEW_CREDIT_CARD_WIDGET_URL =
  'https://cc-widget.oudev.org/previews/pr-177/credit-card.js'

export const SENSEPASS_WIDGET_QUERY_PARAM = 'sensepass_widget'
export const SENSEPASS_WIDGET_VERSION = '1.1.1'
export type SensePassWidgetVersion = typeof SENSEPASS_WIDGET_VERSION

export type SensePassWidgetFormIds =
  | string
  | readonly string[]
  | null
  | undefined

export function normalizeSensePassWidgetFormIds(
  value: unknown
): SensePassWidgetFormIds {
  if (typeof value === 'string') return value

  if (Array.isArray(value)) {
    return value.filter((formId): formId is string => typeof formId === 'string')
  }

  return undefined
}

export function parseSensePassWidgetFormIds(
  value: SensePassWidgetFormIds
): string[] {
  const values: readonly string[] =
    typeof value === 'string' ? value.split(',') : value ?? []

  return values
    .map(formId => formId.trim())
    .filter(Boolean)
}

export function isSensePassWidgetFormIdAllowed(
  formId: string | undefined,
  allowlistedFormIds: SensePassWidgetFormIds
): boolean {
  const normalizedFormId = formId?.trim()

  return Boolean(
    normalizedFormId &&
      parseSensePassWidgetFormIds(allowlistedFormIds).includes(normalizedFormId)
  )
}

export function resolveSensePassWidgetVersion(
  value: unknown
): SensePassWidgetVersion | undefined {
  return value === SENSEPASS_WIDGET_VERSION
    ? SENSEPASS_WIDGET_VERSION
    : undefined
}

export function resolveSensePassWidgetVersionFromQuery(
  query: Record<string, unknown> | undefined
): SensePassWidgetVersion | undefined {
  return resolveSensePassWidgetVersion(query?.[SENSEPASS_WIDGET_QUERY_PARAM])
}

export function isSensePassWidgetQueryParam(key: string): boolean {
  return key === SENSEPASS_WIDGET_QUERY_PARAM
}

export function removeSensePassWidgetQueryParam(url: string): string {
  const parsedUrl = safeUrl(url)

  if (!parsedUrl) return url

  parsedUrl.searchParams.delete(SENSEPASS_WIDGET_QUERY_PARAM)

  return parsedUrl.toString()
}

function safeUrl(value?: string): URL | null {
  if (!value) return null

  try {
    return new URL(value)
  } catch {
    return null
  }
}

function resolveHostedApiUrl(locationOrigin?: string): string | null {
  const url = safeUrl(locationOrigin)

  if (!url) return null

  if (
    url.hostname === 'localhost' ||
    url.hostname === '127.0.0.1' ||
    url.hostname === '0.0.0.0'
  ) {
    return url.origin
  }

  if (url.hostname === 'v2.donateou.org') {
    return DEV_API_URL
  }

  if (url.hostname === 'donate-v2.oudev.org') {
    return DEV_TEST_API_URL
  }

  if (url.hostname === 'donate-v2.oudev.org') {
    return STAGE_API_URL
  }

  if (url.hostname === 'v2.donateou.org') {
    return PROD_API_URL
  }

  return null
}

function resolveEmbeddedApiUrl(scriptSrc?: string): string | null {
  const url = safeUrl(scriptSrc)

  if (!url) return null

  const href = url.href

  if (
    url.hostname === 'v2.donateou.org' ||
    href.includes('d3f1x7meex37wo.cloudfront.net')
  ) {
    return PROD_API_URL
  }

  if (
    url.hostname === 'v2.donateou.org' ||
    href.includes('dsp6jiecnlr2s.cloudfront.net') ||
    href.includes('/dev/')
  ) {
    return DEV_API_URL
  }

  if (url.hostname === 'donate-v2.oudev.org') {
    return DEV_TEST_API_URL
  }

  if (
    url.hostname === 'donate-v2.oudev.org' ||
    url.hostname.endsWith('.oudev.org') ||
    href.includes('d1mg0a4gaqywn7.cloudfront.net')
  ) {
    return STAGE_API_URL
  }

  return null
}

function isProductionModeApiUrl(apiUrl: string) {
  return apiUrl === PROD_API_URL || apiUrl === DEV_API_URL
}

function resolveEnvironmentApiUrl(nodeEnv?: string): string {
  switch (nodeEnv) {
    case 'production':
      return PROD_API_URL
    case 'development':
      return DEV_TEST_API_URL
    case 'staging':
    default:
      return STAGE_API_URL
  }
}

function parseEnvironmentBoolean(value?: string): boolean | undefined {
  if (value === undefined) return undefined

  const normalizedValue = value.trim().toLowerCase()

  if (['1', 'true', 'yes'].includes(normalizedValue)) return true
  if (['0', 'false', 'no'].includes(normalizedValue)) return false

  return undefined
}

function resolveEnvironmentTestMode(nodeEnv?: string, paymentApiTestMode?: string): boolean {
  const explicitPaymentApiTestMode = parseEnvironmentBoolean(paymentApiTestMode)
  if (explicitPaymentApiTestMode !== undefined) return explicitPaymentApiTestMode

  return nodeEnv !== 'production'
}

function buildRuntime(apiUrl: string, explicitTestMode?: boolean): ClientRuntime {
  const testMode = explicitTestMode ?? !isProductionModeApiUrl(apiUrl)
  const isProduction = !testMode

  return {
    apiUrl,
    isProduction,
    testMode,
    gtmId: isProduction ? PROD_GTM_ID : STAGING_GTM_ID
  }
}

export function resolveClientRuntime(input: ResolveClientRuntimeInput): ClientRuntime {
  const hostedApiUrl = resolveHostedApiUrl(input.locationOrigin)
  const embeddedApiUrl = resolveEmbeddedApiUrl(input.scriptSrc)
  const contextApiUrl =
    (hostedApiUrl && hostedApiUrl !== PROD_API_URL ? hostedApiUrl : null) ||
    embeddedApiUrl ||
    hostedApiUrl

  const apiUrl =
    (input.explicitTestMode === true ? DEV_TEST_API_URL : null) ||
    contextApiUrl ||
    (input.explicitTestMode === false ? PROD_API_URL : STAGE_API_URL)

  return buildRuntime(apiUrl)
}

export function resolveServerRuntime(input: ResolveServerRuntimeInput = {}): ClientRuntime {
  const nodeEnv = input.nodeEnv ?? (typeof process !== 'undefined' ? process.env.NODE_ENV : undefined)
  const paymentApiTestMode = input.paymentApiTestMode ?? (typeof process !== 'undefined' ? process.env.PAYMENT_API_TEST_MODE : undefined)
  const apiUrl =
    resolveHostedApiUrl(input.refererUrl) ||
    resolveEnvironmentApiUrl(nodeEnv)

  return buildRuntime(apiUrl, resolveEnvironmentTestMode(nodeEnv, paymentApiTestMode))
}

export function resolveCreditCardBooleanAttributes(input: boolean | CreditCardBooleanAttributesInput) {
  const flags = typeof input === 'boolean' ? { testMode: input } : input
  const attributes: Record<string, ''> = {}

  if (flags.testMode) attributes.testmode = ''
  if (flags.recurring) attributes.recurring = ''
  if (flags.noCVV) attributes.nocvv = ''

  return attributes
}

export function resolveCreditCardWidgetUrl(
  testMode: boolean,
  formId?: string,
  allowlistedFormIds?: SensePassWidgetFormIds,
  requestedVersion?: SensePassWidgetVersion
) {
  if (requestedVersion === SENSEPASS_WIDGET_VERSION) {
    return SENSEPASS_PREVIEW_CREDIT_CARD_WIDGET_URL
  }

  if (testMode) return TEST_CREDIT_CARD_WIDGET_URL

  return (
    isSensePassWidgetFormIdAllowed(formId, allowlistedFormIds)
  )
    ? SENSEPASS_PREVIEW_CREDIT_CARD_WIDGET_URL
    : PROD_CREDIT_CARD_WIDGET_URL
}
